<script setup>
import { ref, computed, onMounted, onUnmounted, onActivated, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AlertResponza from 'dashboard/components-next/responza/AlertResponza.vue';
import WhatsAppPreview from '../../../bulkmessage/components/MessagePreview/WhatsAppPreview.vue';
import { useAlert } from 'dashboard/composables';

const router = useRouter();
const route = useRoute();
// const { t } = useI18n();

// Get accountId from query parameters for filtering
const filterByAccountId = route.query.accountId;

// State
const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const searchQuery = ref('');
const activeLanguage = ref('pt_BR');
const selectedCategory = ref('all');
const selectedStatus = ref('all');
const lastUpdated = ref(null);
const canSync = ref(true);
const nextSyncAt = ref(null);
const cooldownTimer = ref(null);
const cooldownSeconds = ref(0);
const selectedInboxId = ref(null);
const availableInboxes = ref([]);
const showInboxSelector = ref(false);
const showDeleteConfirmation = ref(false);
const templateToDelete = ref(null);
const selectedTemplate = ref(null);
const deletingTemplateId = ref(null);

// Template categories
const categories = [
  { id: 'all', name: 'Todas as categorias' },
  { id: 'MARKETING', name: 'Marketing' },
  { id: 'UTILITY', name: 'Utilidade' },
  { id: 'AUTHENTICATION', name: 'Autenticação' },
];

// Languages supported by WhatsApp
const languages = [
  { code: 'all', name: 'Todos os idiomas' },
  { code: 'pt_BR', name: 'Português (BR)' },
  { code: 'en_US', name: 'Inglês (EUA)' },
  { code: 'es_ES', name: 'Espanhol' },
];

const LANGUAGE_LABELS = languages.reduce((labels, language) => {
  if (language.code !== 'all') {
    labels[language.code] = language.name;
  }
  return labels;
}, {});

const STATUS_SUMMARY_META = {
  APPROVED: {
    badgeLabel: 'Aprovado',
  },
  PENDING: {
    badgeLabel: 'Pendente',
  },
  REJECTED: {
    badgeLabel: 'Rejeitado',
  },
  OTHER: {
    badgeLabel: 'Outro status',
  },
};

// Template statuses
const statuses = [
  { id: 'all', name: 'Todos os status' },
  { id: 'APPROVED', name: 'Aprovado' },
  { id: 'PENDING', name: 'Pendente' },
  { id: 'REJECTED', name: 'Rejeitado' },
  { id: 'OTHER', name: 'Outro status' },
];

// Filtered templates based on search, language, category and status
const filteredTemplates = computed(() => {
  let result = templates.value;

  // Filter by language
  if (activeLanguage.value !== 'all') {
    result = result.filter(t => t.language === activeLanguage.value);
  }

  // Filter by category
  if (selectedCategory.value !== 'all') {
    result = result.filter(t => t.category === selectedCategory.value);
  }

  // Filter by status
  if (selectedStatus.value !== 'all') {
    result = result.filter(t => {
      if (selectedStatus.value === 'OTHER') {
        return !['APPROVED', 'PENDING', 'REJECTED'].includes(t.status);
      }
      return t.status === selectedStatus.value;
    });
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      t =>
        t.name.toLowerCase().includes(query) ||
        getTemplateContent(t).toLowerCase().includes(query)
    );
  }

  return result;
});

const filtersActive = computed(() => {
  return (
    activeLanguage.value !== 'all' ||
    selectedCategory.value !== 'all' ||
    selectedStatus.value !== 'all' ||
    Boolean(searchQuery.value)
  );
});

const activeFilterSummary = computed(() => {
  const summary = [];

  if (searchQuery.value) {
    summary.push(`Busca: "${searchQuery.value}"`);
  }

  if (activeLanguage.value !== 'all') {
    const languageLabel = languages.find(lang => lang.code === activeLanguage.value)?.name;
    summary.push(`Idioma: ${languageLabel || activeLanguage.value}`);
  }

  if (selectedCategory.value !== 'all') {
    const categoryLabel = categories.find(category => category.id === selectedCategory.value)?.name;
    summary.push(`Categoria: ${categoryLabel || selectedCategory.value}`);
  }

  if (selectedStatus.value !== 'all') {
    const statusLabel = statuses.find(status => status.id === selectedStatus.value)?.name;
    summary.push(`Status: ${statusLabel || selectedStatus.value}`);
  }

  return summary.join(' | ');
});

const statusCounts = computed(() => {
  return templates.value.reduce(
    (counts, template) => {
      const key = STATUS_SUMMARY_META[template.status]
        ? template.status
        : 'OTHER';

      counts[key] = (counts[key] || 0) + 1;
      counts.total += 1;
      return counts;
    },
    { APPROVED: 0, PENDING: 0, REJECTED: 0, OTHER: 0, total: 0 }
  );
});

// Get template content from components
const getTemplateContent = template => {
  if (!template.components) return '';

  let content = '';
  template.components.forEach(component => {
    if (component.type === 'HEADER' && component.text) {
      content += `*${component.text}*\n\n`;
    } else if (component.type === 'BODY' && component.text) {
      content += component.text + '\n\n';
    } else if (component.type === 'FOOTER' && component.text) {
      content += `_${component.text}_`;
    }
  });

  return content.trim();
};

// Get language label
const getLanguageLabel = code => {
  return LANGUAGE_LABELS[code] || code;
};

// Get authentication headers from cookie
const getAuthHeaders = () => {
  const getCookie = name => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
      'Content-Type': 'application/json',
    };
  } catch (e) {
    console.error('Error parsing session cookie:', e);
    return {};
  }
};

// Fetch WhatsApp inboxes to get the first available one or use filtered account
const fetchWhatsAppInboxes = async () => {
  try {
    const response = await fetch('/api/v1/bulk_messages/load_inboxes', {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      const officialInboxes = data.results.filter(
        inbox => inbox.integration_type === 'official'
      );

      availableInboxes.value = officialInboxes.map(inbox => ({
        inbox_id: inbox.inbox_id,
        inbox_name: inbox.inbox_name || `WhatsApp ${inbox.display_phone_number || inbox.inbox_id}`,
        display_phone_number: inbox.display_phone_number,
        integration_id: inbox.integration_id
      }));

      // If filterByAccountId is provided, use it directly
      if (filterByAccountId) {
        selectedInboxId.value = parseInt(filterByAccountId);
      } else if (officialInboxes.length > 0) {
        // Otherwise, use the first available official WhatsApp inbox
        selectedInboxId.value = officialInboxes[0].inbox_id;
      }
    }
  } catch (error) {
    console.error('Failed to fetch inboxes:', error);
  }
};

// Fetch templates from API
const fetchTemplates = async () => {
  if (!selectedInboxId.value) {
    templates.value = [];
    return;
  }

  isLoading.value = true;

  try {
    const response = await fetch(
      `/api/v1/bulk_messages/whatsapp_templates/${selectedInboxId.value}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (response.ok) {
      const data = await response.json();
      const processedTemplates = (data.templates || []).map(template => ({
        id: template.id,
        name: template.name,
        status: template.status || 'APPROVED',
        category: template.category || 'UTILITY',
        language: template.language || 'pt_BR',
        components: template.components || [],
        sub_category: template.sub_category,
        parameter_format: template.parameter_format || 'POSITIONAL',
        message_send_ttl_seconds: template.message_send_ttl_seconds,
      }));

      templates.value = processedTemplates;
      lastUpdated.value = data.last_updated;
      canSync.value = data.can_sync;
      nextSyncAt.value = data.next_sync_at;

      if (!canSync.value) {
        startCooldownTimer();
      }
    } else {
      const error = await response.json();
      useAlert(error.error || 'Não foi possível carregar os modelos');
    }
  } catch (error) {
    console.error('Failed to fetch templates:', error);
    useAlert('Não foi possível carregar os modelos');
  } finally {
    isLoading.value = false;
  }
};

// Sync templates manually
const syncTemplates = async () => {
  if (!selectedInboxId.value || !canSync.value) return;

  isSyncing.value = true;

  try {
    const response = await fetch(
      `/api/v1/bulk_messages/whatsapp_templates/${selectedInboxId.value}/sync`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (response.ok) {
      const data = await response.json();
      useAlert(data.message, 'success');

      canSync.value = false;
      nextSyncAt.value = data.next_sync_at;
      startCooldownTimer();

      // Aguardar um pouco mais para garantir que a sincronização foi processada
      // e então recarregar os templates atualizados
      useAlert('Atualizando lista de modelos...', 'info');

      await new Promise(resolve => setTimeout(resolve, 3000));

      // Recarregar templates após sincronização
      await fetchTemplates();

      useAlert('Modelos sincronizados e atualizados com sucesso!', 'success');
    } else {
      const error = await response.json();
      if (response.status === 429) {
        canSync.value = false;
        cooldownSeconds.value = error.remaining_seconds || 60;
        nextSyncAt.value = error.next_sync_at;
        startCooldownTimer();
        useAlert(
          `A sincronização dos modelos está em intervalo. Aguarde ${error.remaining_seconds} segundos.`
        );
      } else {
        useAlert(error.error || 'Não foi possível sincronizar os modelos');
      }
    }
  } catch (error) {
    console.error('Failed to sync templates:', error);
    useAlert('Não foi possível sincronizar os modelos');
  } finally {
    isSyncing.value = false;
  }
};

// Start cooldown timer
const startCooldownTimer = () => {
  if (cooldownTimer.value) {
    clearInterval(cooldownTimer.value);
  }

  const updateCooldown = () => {
    if (nextSyncAt.value) {
      const now = new Date();
      const nextSync = new Date(nextSyncAt.value);
      const remaining = Math.max(0, Math.ceil((nextSync - now) / 1000));

      cooldownSeconds.value = remaining;

      if (remaining <= 0) {
        canSync.value = true;
        clearInterval(cooldownTimer.value);
        cooldownTimer.value = null;
      }
    }
  };

  updateCooldown();
  cooldownTimer.value = setInterval(updateCooldown, 1000);
};

// Auto-sync templates after creation (triggered by query parameter)
const autoSyncAfterCreation = async () => {
  if (!selectedInboxId.value || !canSync.value) {
    // If sync is on cooldown, just fetch templates without syncing
    await fetchTemplates();
    return;
  }

  try {
    const response = await fetch(
      `/api/v1/bulk_messages/whatsapp_templates/${selectedInboxId.value}/sync`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (response.ok) {
      const data = await response.json();
      canSync.value = false;
      nextSyncAt.value = data.next_sync_at;
      startCooldownTimer();

      useAlert('Atualizando lista de modelos...', 'info');
      await new Promise(resolve => setTimeout(resolve, 3000));

      await fetchTemplates();
      useAlert('Modelos sincronizados e atualizados com sucesso!', 'success');
    } else {
      // If sync fails, just fetch templates without syncing
      await fetchTemplates();
    }
  } catch (error) {
    console.error('Error auto-syncing templates:', error);
    // If sync fails, just fetch templates without syncing
    await fetchTemplates();
  }
};

// Select a template for preview
const selectTemplateForPreview = template => {
  selectedTemplate.value = template;
};

// Edit template (navigate to edit page)
const editTemplate = (template, event) => {
  if (event) {
    event.stopPropagation();
  }
  router.push({
    name: 'settings_whatsapp_template_edit',
    params: { templateId: template.id },
    query: { inboxId: selectedInboxId.value }
  });
};

// Generate preview message from template
const getPreviewMessage = computed(() => {
  if (!selectedTemplate.value || !selectedTemplate.value.components) return '';

  const parts = { header: '', body: '', footer: '' };

  selectedTemplate.value.components.forEach(component => {
    if (component.type === 'HEADER' && component.text) {
      parts.header = component.text;
    } else if (component.type === 'BODY' && component.text) {
      parts.body = component.text;
    } else if (component.type === 'FOOTER' && component.text) {
      parts.footer = component.text;
    }
  });

  let message = '';

  if (parts.header) {
    let headerText = parts.header;

    // Get examples from the HEADER component if it has variables
    const headerComponent = selectedTemplate.value.components.find(c => c.type === 'HEADER');
    const headerExamples = headerComponent?.example?.header_text?.[0] || [];

    const headerPlaceholders = headerText.match(/\{\{(\d+)\}\}/g) || [];

    headerPlaceholders.forEach((placeholder) => {
      const numberMatch = placeholder.match(/\{\{(\d+)\}\}/);
      const number = numberMatch ? parseInt(numberMatch[1]) : 1;
      const exampleValue = headerExamples[number - 1] || `Exemplo ${number}`;
      headerText = headerText.replace(placeholder, exampleValue);
    });

    message += `**${headerText}**\n\n`;
  }

  if (parts.body) {
    // Replace placeholders with example values from the template
    let bodyText = parts.body;

    // Get examples from the BODY component
    const bodyComponent = selectedTemplate.value.components.find(c => c.type === 'BODY');
    const examples = bodyComponent?.example?.body_text?.[0] || [];

    const placeholders = bodyText.match(/\{\{(\d+)\}\}/g) || [];

    placeholders.forEach((placeholder) => {
      // Extract the number from {{X}}
      const numberMatch = placeholder.match(/\{\{(\d+)\}\}/);
      const number = numberMatch ? parseInt(numberMatch[1]) : 1;

      // Use the real example value, or fallback to "Exemplo X"
      const exampleValue = examples[number - 1] || `Exemplo ${number}`;
      bodyText = bodyText.replace(placeholder, exampleValue);
    });

    message += bodyText;
  }

  if (parts.footer) {
    message += `\n\n__FOOTER__${parts.footer}__FOOTER_END__`;
  }

  return message;
});

// Generate preview buttons from template
const getPreviewButtons = computed(() => {
  if (!selectedTemplate.value || !selectedTemplate.value.components) return [];

  const buttonComponent = selectedTemplate.value.components.find(c => c.type === 'BUTTONS');
  if (!buttonComponent || !buttonComponent.buttons) return [];

  return buttonComponent.buttons.map(button => ({
    type: button.type,
    text: button.text,
    url: button.url,
    phone_number: button.phone_number,
  }));
});

// Generate preview attachments from template
const getPreviewAttachments = computed(() => {
  if (!selectedTemplate.value || !selectedTemplate.value.components) return [];

  const headerComponent = selectedTemplate.value.components.find(c => c.type === 'HEADER');
  if (!headerComponent) return [];

  // Handle header media
  if (headerComponent.format === 'IMAGE' && headerComponent.example?.header_handle) {
    return [
      {
        id: 'header-image',
        file_type: 'image',
        data_url: headerComponent.example.header_handle[0] || 'https://via.placeholder.com/400x300',
      },
    ];
  }

  if (headerComponent.format === 'VIDEO' && headerComponent.example?.header_handle) {
    return [
      {
        id: 'header-video',
        file_type: 'video',
        data_url: headerComponent.example.header_handle[0] || '',
      },
    ];
  }

  if (headerComponent.format === 'DOCUMENT' && headerComponent.example?.header_handle) {
    return [
      {
        id: 'header-document',
        file_type: 'file',
        data_url: headerComponent.example.header_handle[0] || '',
        file_name: 'Documento.pdf',
      },
    ];
  }

  return [];
});

// Show delete confirmation dialog
const showDeleteDialog = (template, event) => {
  // Prevent the card click event from firing
  event.stopPropagation();
  templateToDelete.value = template;
  showDeleteConfirmation.value = true;
};

// Cancel delete
const cancelDelete = () => {
  showDeleteConfirmation.value = false;
  templateToDelete.value = null;
};

// Confirm and delete template
const confirmDelete = async () => {
  if (!templateToDelete.value) return;

  // Store the template to delete and close modal immediately
  const templateToDeleteData = templateToDelete.value;
  showDeleteConfirmation.value = false;
  templateToDelete.value = null;

  // Set loading state
  deletingTemplateId.value = templateToDeleteData.id;

  try {
    // Use template name as URL parameter and optionally send template_id as query parameter
    const templateName = encodeURIComponent(templateToDeleteData.name);
    const templateId = templateToDeleteData.id;

    const url = new URL(`/api/v1/whatsapp/${selectedInboxId.value}/templates/${templateName}`, window.location.origin);
    if (templateId) {
      url.searchParams.append('template_id', templateId);
    }

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const result = await response.json();
      useAlert(result.message || 'Template excluído com sucesso!', 'success');

      // Remove the template from the local array (optimistic update)
      templates.value = templates.value.filter(t => t.id !== templateToDeleteData.id);

      // If the deleted template was selected, clear or select the next one
      if (selectedTemplate.value?.id === templateToDeleteData.id) {
        selectedTemplate.value = templates.value.length > 0 ? templates.value[0] : null;
      }
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao excluir template');
    }
  } catch (error) {
    console.error('Failed to delete template:', error);
    useAlert(error.message || 'Erro ao excluir template', 'error');
  } finally {
    deletingTemplateId.value = null;
  }
};

// Format date for display
const formatDate = dateString => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
};

// Clear filters
const clearFilters = () => {
  searchQuery.value = '';
  activeLanguage.value = 'all';
  selectedCategory.value = 'all';
  selectedStatus.value = 'all';
};

// Get current inbox name for display
const getCurrentInboxName = computed(() => {
  if (filterByAccountId) {
    const inbox = availableInboxes.value.find(i => i.inbox_id.toString() === filterByAccountId);
    return inbox ? inbox.inbox_name : `Account ID: ${filterByAccountId}`;
  }

  const currentInbox = availableInboxes.value.find(i => i.inbox_id === selectedInboxId.value);
  return currentInbox ? currentInbox.inbox_name : 'Modelos do WhatsApp';
});

// Switch to different inbox
const switchInbox = (inboxId) => {
  selectedInboxId.value = inboxId;
  showInboxSelector.value = false;
  fetchTemplates();
};

// Toggle inbox selector dropdown
const toggleInboxSelector = () => {
  showInboxSelector.value = !showInboxSelector.value;
};

// Go back to previous page
const goBack = () => {
  // If filtered by account, go back to that account's profile
  if (filterByAccountId) {
    router.push({
      name: 'settings_whatsapp_account_profile',
      params: { inboxId: filterByAccountId }
    });
  } else {
    // Otherwise go back to accounts list
    router.push({ name: 'settings_whatsapp_accounts' });
  }
};

// Navigate to template creation page
const createTemplate = () => {
  if (!selectedInboxId.value) {
    useAlert('Selecione uma caixa de entrada primeiro', 'warning');
    return;
  }

  router.push({
    name: 'settings_whatsapp_template_create',
    query: { inboxId: selectedInboxId.value }
  });
};

// Auto-select first template when filtered templates change
watch(filteredTemplates, (newTemplates) => {
  // If there are templates and no template is selected, select the first one
  if (newTemplates.length > 0 && !selectedTemplate.value) {
    selectedTemplate.value = newTemplates[0];
  }
  // If the currently selected template is not in the filtered list, select the first one
  else if (newTemplates.length > 0 && selectedTemplate.value) {
    const stillExists = newTemplates.find(t => t.id === selectedTemplate.value.id);
    if (!stillExists) {
      selectedTemplate.value = newTemplates[0];
    }
  }
  // If there are no templates, clear the selection
  else if (newTemplates.length === 0) {
    selectedTemplate.value = null;
  }
});

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.inbox-selector') && showInboxSelector.value) {
    showInboxSelector.value = false;
  }
};

// Add event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Initialize on mount
onMounted(async () => {
  await fetchWhatsAppInboxes();
  if (selectedInboxId.value) {
    await fetchTemplates();
  }
});

// Auto-sync templates when returning from template creation
onActivated(async () => {
  const syncParam = route.query.syncTemplates;

  if (syncParam === 'true') {
    // Remove the syncTemplates parameter from URL
    const newQuery = { ...route.query };
    delete newQuery.syncTemplates;
    router.replace({ query: newQuery });

    // Trigger auto-sync
    await autoSyncAfterCreation();
  }
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('WHATSAPP_SETTINGS.TEMPLATES.TITLE')"
        :description="$t('WHATSAPP_SETTINGS.TEMPLATES.DESCRIPTION')"
        feature-name="whatsapp"
      />
    </template>
    <template #body>
      <div class="space-y-6">
        <div>
          <Button
            label="Voltar"
            variant="ghost"
            color="slate"
            size="sm"
            @click="goBack"
          >
            <template #icon>
              <i class="i-lucide-arrow-left" />
            </template>
          </Button>
        </div>

        <div class="rounded-xl border border-n-slate-3 bg-white p-4 shadow-sm dark:bg-n-slate-2">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div class="space-y-3">
              <div class="flex flex-wrap items-center gap-3">
                <span class="inline-flex items-center gap-2 text-lg font-semibold text-n-slate-12">
                  <i class="i-lucide-message-circle text-n-blue-text" />
                  {{ getCurrentInboxName }}
                </span>

                <div
                  v-if="!filterByAccountId && availableInboxes.length > 1"
                  class="relative inbox-selector"
                >
                  <button
                    class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-slate-3 bg-n-slate-1 px-3 text-sm text-n-slate-12 transition-colors hover:border-n-blue-border focus:outline-none dark:border-n-slate-6 dark:bg-n-slate-3"
                    @click.stop="toggleInboxSelector"
                  >
                    <span class="flex items-center gap-2 truncate">
                      <i class="i-lucide-chevrons-up-down text-n-slate-11" />
                      <span class="truncate">{{ getCurrentInboxName }}</span>
                    </span>
                    <i
                      class="i-lucide-chevron-down transition-transform"
                      :class="{ 'rotate-180': showInboxSelector }"
                    />
                  </button>

                  <div
                    v-if="showInboxSelector"
                    class="absolute z-50 mt-2 w-64 overflow-hidden rounded-lg border border-n-slate-3 bg-white shadow-lg dark:border-n-slate-6 dark:bg-n-slate-2"
                  >
                    <div class="max-h-60 overflow-y-auto py-1">
                      <button
                        v-for="inbox in availableInboxes"
                        :key="inbox.inbox_id"
                        type="button"
                        class="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-n-slate-1 dark:hover:bg-n-slate-4"
                        @click="switchInbox(inbox.inbox_id)"
                      >
                        <span class="flex h-8 w-8 items-center justify-center rounded-full bg-grass-300 text-grass-900">
                          <i class="i-lucide-message-circle" />
                        </span>
                        <div class="flex-1">
                          <div class="text-sm font-medium text-n-slate-12">
                            {{ inbox.inbox_name }}
                          </div>
                          <div
                            v-if="inbox.display_phone_number"
                            class="text-xs text-n-slate-11"
                          >
                            {{ inbox.display_phone_number }}
                          </div>
                        </div>
                        <i
                          v-if="inbox.inbox_id === selectedInboxId"
                          class="i-lucide-check text-grass-700 text-sm"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-n-slate-11">
                <span>Última atualização: {{ formatDate(lastUpdated) }}</span>
                <span v-if="!canSync && cooldownSeconds > 0">
                  Próxima sincronização disponível em {{ cooldownSeconds }}s
                </span>
                <span
                  v-if="filterByAccountId"
                  class="inline-flex items-center gap-1 rounded-full bg-n-slate-2 px-2 py-0.5 text-xs text-n-slate-11 dark:bg-n-slate-4"
                >
                  <i class="i-lucide-info" />
                  Exibindo modelos para a conta selecionada
                </span>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <Button
                label="Criar modelo"
                variant="solid"
                color="blue"
                size="sm"
                @click="createTemplate"
              >
                <template #icon>
                  <i class="i-lucide-plus" />
                </template>
              </Button>

              <Button
                :label="
                  isSyncing
                    ? 'Sincronizando...'
                    : canSync
                      ? 'Sincronizar modelos'
                      : `Aguarde ${cooldownSeconds}s`
                "
                :variant="canSync ? 'solid' : 'outline'"
                color="blue"
                size="sm"
                :disabled="!canSync || isSyncing || !selectedInboxId"
                @click="syncTemplates"
              >
                <template #icon>
                  <i
                    class="i-lucide-refresh-cw"
                    :class="{ 'animate-spin': isSyncing }"
                  />
                </template>
              </Button>
            </div>
          </div>
        </div>

        <div
          v-if="!selectedInboxId"
          class="rounded-xl border border-dashed border-n-slate-4 bg-n-slate-1 p-6 text-center text-sm text-n-slate-11 dark:bg-n-slate-3"
        >
          Nenhuma caixa de entrada do WhatsApp encontrada. Configure um canal do WhatsApp primeiro.
        </div>

        <template v-else>
          <!-- Compact Stats Bar -->
          <div
            v-if="statusCounts.total > 0"
            class="flex flex-wrap items-center gap-2 rounded-lg border border-n-slate-3 bg-n-slate-1 px-4 py-2.5 dark:border-n-slate-6 dark:bg-n-slate-3"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ statusCounts.total }} {{ statusCounts.total === 1 ? 'modelo' : 'modelos' }}
            </span>
            <span class="text-n-slate-4">•</span>

            <div class="flex flex-wrap items-center gap-2">
              <span
                v-if="statusCounts.APPROVED > 0"
                class="inline-flex items-center gap-1 rounded-md bg-grass-50 px-2 py-0.5 text-xs font-medium text-grass-800"
              >
                <i class="i-lucide-check-circle text-[0.7rem]" />
                {{ statusCounts.APPROVED }}
              </span>

              <span
                v-if="statusCounts.PENDING > 0"
                class="inline-flex items-center gap-1 rounded-md bg-n-amber-1 px-2 py-0.5 text-xs font-medium text-n-amber-11"
              >
                <i class="i-lucide-clock text-[0.7rem]" />
                {{ statusCounts.PENDING }}
              </span>

              <span
                v-if="statusCounts.REJECTED > 0"
                class="inline-flex items-center gap-1 rounded-md bg-n-ruby-1 px-2 py-0.5 text-xs font-medium text-n-ruby-11"
              >
                <i class="i-lucide-x-circle text-[0.7rem]" />
                {{ statusCounts.REJECTED }}
              </span>

              <span
                v-if="statusCounts.OTHER > 0"
                class="inline-flex items-center gap-1 rounded-md bg-n-slate-2 px-2 py-0.5 text-xs font-medium text-n-slate-11 dark:bg-n-slate-4"
              >
                <i class="i-lucide-help-circle text-[0.7rem]" />
                {{ statusCounts.OTHER }}
              </span>
            </div>

            <span
              v-if="filtersActive"
              class="ml-auto inline-flex items-center gap-1 rounded-md bg-n-blue-1 px-2 py-0.5 text-xs font-medium text-n-blue-text"
            >
              <i class="i-lucide-filter text-[0.7rem]" />
              {{ filteredTemplates.length }} exibidos
            </span>
          </div>

          <div class="rounded-xl border border-n-slate-3 bg-white p-4 shadow-sm dark:bg-n-slate-2">
            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div class="w-full md:max-w-md">
                <div class="relative">
                  <span
                    class="i-lucide-search absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2 rtl:right-2"
                  />
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="h-10 w-full rounded-lg border border-n-slate-3 bg-n-background text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none ltr:!pl-8 rtl:!pr-8 !px-3 !py-2 dark:border-n-slate-6 dark:bg-n-slate-3 hover:border-n-slate-6 dark:hover:border-n-slate-7 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
                    placeholder="Buscar modelos..."
                  />
                </div>
                <div v-if="filtersActive" class="mt-2 text-xs text-n-slate-11">
                  Filtros ativos: {{ activeFilterSummary }}
                </div>
              </div>

              <div class="flex w-full flex-col gap-2 sm:flex-row md:w-auto md:gap-3">
                <select
                  v-model="activeLanguage"
                  class="h-10 w-full rounded-lg border border-n-slate-4 bg-n-background px-3 !pr-6 text-sm text-n-slate-12 transition-colors hover:border-n-slate-6 focus:border-n-brand focus:outline-none dark:border-n-slate-6 dark:bg-n-slate-3 sm:w-auto md:min-w-[150px]"
                >
                  <option v-for="lang in languages" :key="lang.code" :value="lang.code">
                    {{ lang.name }}
                  </option>
                </select>

                <select
                  v-model="selectedCategory"
                  class="h-10 w-full rounded-lg border border-n-slate-4 bg-n-background px-3 !pr-6 text-sm text-n-slate-12 transition-colors hover:border-n-slate-6 focus:border-n-brand focus:outline-none dark:border-n-slate-6 dark:bg-n-slate-3 sm:w-auto md:min-w-[150px]"
                >
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.name }}
                  </option>
                </select>

                <select
                  v-model="selectedStatus"
                  class="h-10 w-full rounded-lg border border-n-slate-4 bg-n-background px-3 !pr-6 text-sm text-n-slate-12 transition-colors hover:border-n-slate-6 focus:border-n-brand focus:outline-none dark:border-n-slate-6 dark:bg-n-slate-3 sm:w-auto md:min-w-[150px]"
                >
                  <option
                    v-for="status in statuses"
                    :key="status.id"
                    :value="status.id"
                  >
                    {{ status.name }}
                  </option>
                </select>

                <Button
                  v-if="filtersActive"
                  label="Limpar filtros"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="clearFilters"
                >
                  <template #icon>
                    <i class="i-lucide-x-circle" />
                  </template>
                </Button>
              </div>
            </div>
          </div>

          <div v-if="isLoading" class="flex items-center justify-center py-12">
            <Spinner size="medium" />
          </div>

          <!-- Two-column layout: Templates list + Preview -->
          <div
            v-else-if="filteredTemplates.length > 0"
            class="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,460px]"
          >
            <!-- Templates List -->
            <div class="space-y-2">
              <div
                v-for="template in filteredTemplates"
                :key="template.id"
                class="group cursor-pointer rounded-lg border p-4 transition-all"
                :class="[
                  selectedTemplate?.id === template.id
                    ? 'border-n-brand bg-n-blue-1 dark:bg-n-slate-3'
                    : 'border-n-slate-3 bg-white hover:border-n-blue-border hover:shadow-sm dark:border-n-slate-6 dark:bg-n-slate-2',
                  deletingTemplateId === template.id && 'opacity-50 pointer-events-none'
                ]"
                @click="selectTemplateForPreview(template)"
              >
                <div class="flex items-center justify-between gap-4">
                  <!-- Template Info -->
                  <div class="flex min-w-0 flex-1 items-center gap-4">
                    <!-- Name and ID -->
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <h3 class="truncate text-sm font-semibold text-n-slate-12">
                          {{ template.name }}
                        </h3>
                      </div>
                      <p class="mt-0.5 truncate text-xs text-n-slate-10">
                        ID: {{ template.id }}
                      </p>
                    </div>

                    <!-- Badges -->
                    <div class="flex flex-shrink-0 flex-wrap items-center gap-2">
                      <!-- Status Badge -->
                      <span
                        v-if="template.status === 'APPROVED'"
                        class="inline-flex items-center gap-1 rounded-md bg-grass-50 px-2 py-1 text-xs font-medium text-grass-800"
                      >
                        <i class="i-lucide-check-circle text-[0.7rem]" />
                        Aprovado
                      </span>
                      <span
                        v-else-if="template.status === 'PENDING'"
                        class="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                      >
                        <i class="i-lucide-clock text-[0.7rem]" />
                        Pendente
                      </span>
                      <span
                        v-else-if="template.status === 'REJECTED'"
                        class="inline-flex items-center gap-1 rounded-md bg-ruby-50 px-2 py-1 text-xs font-medium text-ruby-800"
                      >
                        <i class="i-lucide-x-circle text-[0.7rem]" />
                        Rejeitado
                      </span>
                      <span
                        v-else
                        class="inline-flex items-center gap-1 rounded-md bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
                      >
                        {{ template.status }}
                      </span>

                      <!-- Category Badge -->
                      <span
                        v-if="template.category === 'MARKETING'"
                        class="inline-flex items-center gap-1 rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-800"
                      >
                        <i class="i-lucide-megaphone text-[0.7rem]" />
                        Marketing
                      </span>
                      <span
                        v-else-if="template.category === 'UTILITY'"
                        class="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-800"
                      >
                        <i class="i-lucide-settings text-[0.7rem]" />
                        Utilidade
                      </span>
                      <span
                        v-else-if="template.category === 'AUTHENTICATION'"
                        class="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-800"
                      >
                        <i class="i-lucide-shield-check text-[0.7rem]" />
                        Autenticação
                      </span>

                      <!-- Language Badge -->
                      <span
                        class="inline-flex items-center gap-1 rounded-md bg-n-slate-2 px-2 py-1 text-xs font-medium text-n-slate-11 dark:bg-n-slate-4"
                      >
                        <i class="i-lucide-globe-2 text-[0.7rem]" />
                        {{ getLanguageLabel(template.language) }}
                      </span>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-n-blue-text transition-colors hover:bg-n-blue-1 dark:hover:bg-n-slate-3"
                      title="Editar template"
                      @click="editTemplate(template, $event)"
                    >
                      <i class="i-lucide-edit text-sm" />
                    </button>

                    <button
                      type="button"
                      class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-n-ruby-9 transition-colors hover:bg-n-ruby-1 hover:text-n-ruby-11 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Excluir template"
                      :disabled="deletingTemplateId === template.id"
                      @click="showDeleteDialog(template, $event)"
                    >
                      <i
                        v-if="deletingTemplateId === template.id"
                        class="i-lucide-loader-2 text-sm animate-spin"
                      />
                      <i
                        v-else
                        class="i-lucide-trash-2 text-sm"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preview Panel (Sticky) -->
            <div class="hidden lg:block">
              <div class="sticky top-4">
                <div
                  v-if="selectedTemplate"
                  class="overflow-hidden rounded-lg border border-n-slate-3 bg-white dark:border-n-slate-6 dark:bg-n-slate-2"
                >
                  <div class="border-b border-n-slate-3 px-4 py-3 dark:border-n-slate-6">
                    <h3 class="text-sm font-semibold text-n-slate-12">
                      Preview do Template
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ selectedTemplate.name }}
                    </p>
                  </div>

                  <div class="flex justify-center px-2 py-4">
                    <WhatsAppPreview
                      :message="getPreviewMessage"
                      recipient-name="Cliente"
                      :attachments="getPreviewAttachments"
                      :buttons="getPreviewButtons"
                    />
                  </div>
                </div>

                <div
                  v-else
                  class="flex flex-col items-center justify-center rounded-lg border border-dashed border-n-slate-3 bg-n-slate-1 p-8 text-center dark:border-n-slate-6 dark:bg-n-slate-3"
                >
                  <i class="i-lucide-eye-off mb-3 text-3xl text-n-slate-9" />
                  <p class="text-sm text-n-slate-11">
                    Selecione um template para visualizar
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="rounded-xl border border-dashed border-n-slate-4 bg-n-slate-1 p-8 text-center text-sm text-n-slate-11 dark:bg-n-slate-3"
          >
            <div class="mb-4 flex flex-col items-center gap-3">
              <i class="i-lucide-inbox h-8 w-8 text-n-slate-9" />
              <span>
                {{
                  templates.length === 0
                    ? 'Nenhum modelo encontrado. Tente sincronizar os modelos.'
                    : 'Nenhum modelo corresponde aos filtros.'
                }}
              </span>
            </div>

            <div class="flex justify-center gap-2">
              <Button
                v-if="templates.length === 0"
                :label="canSync ? 'Sincronizar modelos' : `Aguarde ${cooldownSeconds}s`"
                variant="solid"
                color="blue"
                size="sm"
                :disabled="!canSync || isSyncing"
                @click="syncTemplates"
              >
                <template #icon>
                  <i
                    class="i-lucide-refresh-cw"
                    :class="{ 'animate-spin': isSyncing }"
                  />
                </template>
              </Button>

              <Button
                v-else
                label="Limpar filtros"
                variant="outline"
                color="slate"
                size="sm"
                @click="clearFilters"
              >
                <template #icon>
                  <i class="i-lucide-eraser" />
                </template>
              </Button>
            </div>
          </div>
        </template>
      </div>

      <!-- Delete Confirmation Dialog -->
      <AlertResponza
        :show="showDeleteConfirmation"
        type="confirmation"
        title="Confirmar exclusão"
        :message="`Tem certeza que deseja excluir o template &quot;${templateToDelete?.name}&quot;? Esta ação não pode ser desfeita.`"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
        @close="cancelDelete"
      />
    </template>
  </SettingsLayout>
</template>
